<!--
  OneOffItemModal.vue
  콜봇 월 정산 · 정산 탭 "일회성 항목"의 추가·수정 모달(화면 명세 2-4 일회성 항목, 요청 계약 4-4).

  필드: 이름(필수) · 금액(0 이상 정수 원, 공급가액 — 부가세 별도) · 메모(선택). 달은 고르지 않는다 —
  추가는 지금 보고 있는 달에 넣고, 수정은 달을 바꾸지 않는다(계약).
  검증 실패는 message.warning() 으로 알린다(antd 폼 규칙을 쓰지 않는다). 통과하면 submit 이벤트로
  { name, amount, memo } 를 넘기고, 호출·확인창·실패 안내는 부모(OneOffItems)가 한다 — 모달은 부모가 닫는다.
  모달 구조는 .cursor/rules/modal.mdc · PbxSiteFormModal.vue 와 같다.
-->
<template>
  <a-modal :open="open" :footer="null" :mask-closable="false" width="480px" @cancel="emit('close')">
    <template #title>
      <div class="modal-title">{{ item ? '일회성 항목 수정' : '일회성 항목 추가' }}</div>
    </template>

    <div class="modal-content">
      <div class="oneoff-form">
        <p class="oneoff-form__lead">
          {{ monthText }} 정산에 사용량과 상관없는 청구를 더합니다. 경고를 만들지 않고 자동 마감을 막지 않으며,
          원가를 관리하지 않아 마진 계산에서 뺍니다.
        </p>
        <p v-if="internal" class="oneoff-form__note">{{ ONE_OFF_INTERNAL_NOTE }}</p>

        <label class="oneoff-form__field">
          <span class="oneoff-form__label">이름 <em>*</em></span>
          <a-input v-model:value="form.name" :maxlength="100" placeholder="예: 상담사 전화기 VVX150 1대" />
        </label>

        <label class="oneoff-form__field">
          <span class="oneoff-form__label">금액 <em>*</em></span>
          <a-input-number
            v-model:value="form.amount"
            class="oneoff-form__amount"
            :min="0"
            :step="1000"
            :formatter="formatThousandsInput"
            :parser="parseThousandsInput"
            addon-after="원"
            placeholder="예: 127,000"
          />
          <span class="oneoff-form__hint">0 이상 정수(원) · 공급가액이며 부가세는 별도입니다.</span>
        </label>

        <label class="oneoff-form__field">
          <span class="oneoff-form__label">메모</span>
          <a-textarea
            v-model:value="form.memo"
            :maxlength="500"
            :auto-size="{ minRows: 2, maxRows: 5 }"
            placeholder="예: 공급사 견적, 부가세 별도"
          />
        </label>
      </div>

      <div class="modal-footer">
        <a-button @click="emit('close')">취소</a-button>
        <a-button type="primary" class="yellow-button" :loading="submitting" @click="submit">
          {{ item ? '저장' : '추가' }}
        </a-button>
      </div>
    </div>
  </a-modal>
</template>

<script setup>
import { computed, reactive, watch } from 'vue';
import { message } from 'ant-design-vue';

import { formatThousandsInput, monthLabel, parseThousandsInput } from '../../service/settlementFormat';
import { ONE_OFF_INTERNAL_NOTE } from '../../service/settlementRules';

const props = defineProps({
  open: { type: Boolean, required: true },
  /** 수정할 항목(OneOffItem) — 추가는 null */
  item: { type: Object, default: null },
  submitting: { type: Boolean, default: false },
  /** 'YYYY-MM' — 안내 문구에만 쓴다 */
  month: { type: String, required: true },
  /** 내부·테스트 회사 */
  internal: { type: Boolean, default: false },
});

const emit = defineEmits(['close', 'submit']);

const form = reactive({ name: '', amount: null, memo: '' });

// 열릴 때마다 초기화 — 닫았다 다시 열면 이전 입력이 남지 않게
watch(
  () => props.open,
  (open) => {
    if (!open) return;
    form.name = props.item?.name ?? '';
    form.amount = typeof props.item?.amount === 'number' ? props.item.amount : null;
    form.memo = props.item?.memo ?? '';
  },
  { immediate: true }
);

const monthText = computed(() => monthLabel(props.month));

/** 검증 — 통과하면 null, 아니면 message.warning 문구 */
function validate() {
  if (!form.name.trim()) return '항목 이름을 입력해 주세요.';
  if (form.amount === null || form.amount === undefined || form.amount === '') return '금액을 입력해 주세요.';
  const amount = Number(form.amount);
  if (!Number.isInteger(amount) || amount < 0) {
    return '금액은 0 이상의 정수(원)로 넣어 주세요. 공급가액(부가세 별도)입니다.';
  }
  return null;
}

function submit() {
  if (props.submitting) return;
  const error = validate();
  if (error) {
    message.warning(error);
    return;
  }
  emit('submit', { name: form.name.trim(), amount: Number(form.amount), memo: form.memo.trim() });
}
</script>

<style scoped lang="scss">
@use '../../styles/settlement' as *;

@include st-modal;
@include yellow-button;

.oneoff-form {
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding-top: 4px;
  font-size: 13px;

  &__lead {
    margin: 0;
    font-size: 12.5px;
    line-height: 1.6;
    color: $st-text-secondary;
  }

  &__note {
    margin: 0;
    padding: 8px 10px;
    border: 1px solid $st-border;
    border-radius: 8px;
    background: $st-bg;
    font-size: 12.5px;
    color: $st-text-secondary;
  }

  &__field {
    display: flex;
    flex-direction: column;
    gap: 6px;
    min-width: 0;
    margin: 0;
  }

  &__label {
    font-size: 13px;
    font-weight: 600;
    color: $st-text;

    em {
      font-style: normal;
      color: $st-danger;
    }
  }

  &__amount {
    width: 100%;
  }

  &__hint {
    font-size: 12px;
    color: $st-muted;
  }
}
</style>
